import React, { useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Container from '@material-ui/core/Container';

import Typography from 'Components/UI/Typography';

import MuiAddCircleIcon from '@material-ui/icons/AddCircle';
import Button from 'Components/UI/Button';
import CancelIcon from '@material-ui/icons/Cancel';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaFormInline from './Components/MetaFormInline';

const useStyles = buildUseStyles('MetaCollectionFormInline', theme => ({
  root: {},
  rootPaddingNone: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
  },
  title: {
    padding: theme.spacing(2, 0),
  },
  forms: {
    display: 'flex',
    flexDirection: 'column',
  },
  form: {
    margin: theme.spacing(0.5, 0),
  },
  formMarginNone: {
    margin: 0,
  },
  firstForm: {
    marginTop: theme.spacing(2),
  },
  lastForm: {
    marginBottom: theme.spacing(2),
  },
  addItemButton: {
    justifyContent: 'left',
  },
  removeItemButton: {
    justifyContent: 'left',
  },
  deleteIcon: {
    marginRight: theme.spacing(1),
  },
  plusIcon: {
    marginRight: theme.spacing(1),
  },
}));

export const getResourceErrorsOverride = (errors, resourceIndex) => {
  if (!errors) {
    return null;
  }
  // For each error, look at the source and see if the current index matches the current index in the error
  // If one doesn't exist, skip it. If the error is appropriate, set the source to everything after the index
  const resourceErrorsOverride = [];
  for (let errorIndex = 0; errorIndex < errors.length; errorIndex++) {
    const match = /.*\[(\d+)\]\.?(.*)?/.exec(errors[errorIndex]?.source);

    if (match) {
      const indexInErrorSource = parseInt(match[1], 10);

      const restOfErrorSource = match[2];
      if (!Number.isNaN(indexInErrorSource) && indexInErrorSource === resourceIndex) {
        const resourceError = Object.assign({}, errors[errorIndex]);
        resourceError.source = restOfErrorSource;
        resourceErrorsOverride.push(resourceError);
      }
    }
  }

  return resourceErrorsOverride;
};

/**
 *
 * @deprecated Use MetaCollectionRenderer
 */
export default function MetaCollectionFormInline({
  className,
  collection,
  title,
  ContainerProps,
  FormProps,
  TitleProps,
  AddButtonProps,
  onAdd,
  onRemove,
  addButtonText,
  padding,
  margin,
  metaOverrideSet,
  metaOverrideConfig,
  errorsOverride,
}) {
  const theme = useTheme();
  const classes = useStyles(theme);

  const resourceList = collection.resources().list();
  const resourceCount = resourceList.length;

  // If errors exist > scroll to first form element with errors
  useEffect(() => {
    const errorElementMessageSelector = '.MuiFormControl-root .Mui-error';
    if (errorsOverride) {
      // Get first error element / scroll
      const firstErrorElementParentNode = document.querySelector(errorElementMessageSelector).parentNode;

      if (firstErrorElementParentNode) {
        // Get y-cord of error element to scroll to
        const yCord = firstErrorElementParentNode.getBoundingClientRect().top + window.scrollY;

        // Scroll to first error
        window.scrollTo({
          top: yCord,
          behavior: 'smooth',
        });
      }
    }
  }, [errorsOverride]);

  return (
    <Container
      className={classNames(classes.root, className, {
        [classes.rootPaddingNone]: padding === 'none',
      })}
      maxWidth={false}
      {...ContainerProps}
    >
      {title && (
        <Typography
          className={classes.title}
          color="primary"
          variant="h2"
          {...TitleProps}
        >
          {title}
        </Typography>
      )}
      <div className={classes.forms}>
        {resourceList.map((resource, i) => {
          const resourceErrorsOverride = getResourceErrorsOverride(errorsOverride, i);
          return (
            <React.Fragment
              key={`${resource.id()}-${i}`} // eslint-disable-line react/no-array-index-key
            >
              <MetaFormInline
                className={classNames(classes.form, {
                  [classes.formMarginNone]: margin === 'none',
                  [classes.firstForm]: margin === 'default' && i === 0,
                  [classes.lastForm]: margin === 'default' && i === resourceCount - 1,
                })}
                padding={padding}
                margin={margin}
                onAdd={onAdd}
                onRemove={onRemove}
                resource={resource}
                {...FormProps}
                index={i}
                metaOverrideSet={typeof metaOverrideSet === 'function' ? metaOverrideSet(resource, metaOverrideConfig) : metaOverrideSet}
                errorsOverride={resourceErrorsOverride?.length > 0 ? resourceErrorsOverride : null}
              />
              {onRemove && (
                <Button
                  onClick={() => {
                    collection.resources().removeItemByIndex(i);
                    onRemove();
                  }}
                  className={classes.removeItemButton}
                  color="red"
                >
                  <CancelIcon className={classes.deleteIcon} />
                  <Typography>Remove</Typography>
                </Button>
              )}
            </React.Fragment>
          );
        })}
        {onAdd && (
          <Button
            color="secondary"
            onClick={onAdd}
            className={classes.addItemButton}
            {...AddButtonProps}
          >
            <MuiAddCircleIcon className={classes.plusIcon} />
            {addButtonText}
          </Button>
        )}
      </div>
    </Container>
  );
}

MetaCollectionFormInline.defaultProps = {
  className: '',
  title: null,
  ContainerProps: {},
  FormProps: {},
  TitleProps: {},
  AddButtonProps: {},
  onAdd: null,
  onRemove: null,
  addButtonText: 'Add New',
  metaOverrideSet: null,
  errorsOverride: null,
  metaOverrideConfig: {},
  padding: 'default',
  margin: 'default',
};

MetaCollectionFormInline.propTypes = {
  className: PropTypes.string,
  collection: PropTypes.object.isRequired,
  title: PropTypes.string,
  ContainerProps: PropTypes.object,
  FormProps: PropTypes.object,
  TitleProps: PropTypes.object,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  addButtonText: PropTypes.string,
  AddButtonProps: PropTypes.object,
  metaOverrideSet: PropTypes.object,
  errorsOverride: PropTypes.array,
  metaOverrideConfig: PropTypes.object,
  padding: PropTypes.oneOf(['default', 'none']),
  margin: PropTypes.oneOf(['default', 'none']),
};
